import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Search } from "lucide-react";
import StatusBadge from "@/components/StatusBadge";
import Pagination from "@/components/Pagination";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { MobileCards, DataCard, EmptyCard } from "@/components/DataCards";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";

const STATUSES = ["todos", "aberto", "em_atendimento", "aguardando_usuario", "aguardando_equipe", "resolvido", "fechado"];

export default function SupportTickets() {
  const navigate = useNavigate();
  const [data, setData] = useState({ items: [], total: 0, page: 1, limit: 20 });
  const [status, setStatus] = useState("todos");
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const limit = 20;

  const load = (p = page, term = q, st = status) => {
    const params = new URLSearchParams({ page: p, limit });
    if (st !== "todos") params.set("status", st);
    if (term.trim()) params.set("q", term.trim());
    return api.get(`/support/staff/tickets?${params}`).then(({ data }) => setData(data)).catch(() => {});
  };
  useEffect(() => { load(page, q, status); /* eslint-disable-next-line */ }, [page, status]);
  useEffect(() => {
    const t = setTimeout(() => { setPage(1); load(1, q, status); }, 300);
    return () => clearTimeout(t); /* eslint-disable-next-line */
  }, [q]);

  const assign = async (id) => {
    try { await api.post(`/support/staff/tickets/${id}/assign`); toast.success("Ticket assumido"); navigate(`/suporte/tickets/${id}`); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-6" data-testid="support-tickets">
      <h2 className="font-heading text-xl font-bold tracking-tight sm:text-2xl">Central de atendimento</h2>
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative w-full sm:w-72">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar por assunto ou e-mail..." className="pl-9" data-testid="staff-search-input" />
        </div>
        <Select value={status} onValueChange={(v) => { setStatus(v); setPage(1); }}>
          <SelectTrigger className="w-52" data-testid="staff-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s.replace(/_/g, " ")}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      <MobileCards testid="staff-ticket-cards">
        {data.items.length === 0 && <EmptyCard>Nenhum ticket.</EmptyCard>}
        {data.items.map((t) => (
          <DataCard
            key={t.id}
            testid={`staff-ticket-card-${t.id}`}
            title={t.subject}
            subtitle={t.user_email}
            badge={<StatusBadge status={t.status} />}
            rows={[
              { label: "Categoria", value: t.category },
              { label: "Atualizado", value: new Date(t.updated_at).toLocaleString("pt-BR") },
            ]}
            actions={
              <>
                <Button variant="outline" size="sm" className="flex-1" onClick={() => navigate(`/suporte/tickets/${t.id}`)} data-testid={`staff-open-m-${t.id}`}>Abrir</Button>
                <Button size="sm" className="flex-1" onClick={() => assign(t.id)} data-testid={`staff-assign-m-${t.id}`}>Assumir</Button>
              </>
            }
          />
        ))}
      </MobileCards>

      <div className="hidden rounded-lg border border-border bg-card md:block">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Assunto</TableHead><TableHead>Usuário</TableHead><TableHead>Categoria</TableHead><TableHead>Status</TableHead><TableHead>Atualizado</TableHead><TableHead className="text-right">Ações</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {data.items.length === 0 && <TableRow><TableCell colSpan={6} className="py-10 text-center text-muted-foreground">Nenhum ticket.</TableCell></TableRow>}
            {data.items.map((t) => (
              <TableRow key={t.id} data-testid={`staff-ticket-${t.id}`}>
                <TableCell className="font-medium">{t.subject}</TableCell>
                <TableCell className="text-muted-foreground">{t.user_email}</TableCell>
                <TableCell>{t.category}</TableCell>
                <TableCell><StatusBadge status={t.status} /></TableCell>
                <TableCell className="text-muted-foreground">{new Date(t.updated_at).toLocaleString("pt-BR")}</TableCell>
                <TableCell className="text-right space-x-2">
                  <Button variant="outline" size="sm" onClick={() => navigate(`/suporte/tickets/${t.id}`)} data-testid={`staff-open-${t.id}`}>Abrir</Button>
                  <Button size="sm" onClick={() => assign(t.id)} data-testid={`staff-assign-${t.id}`}>Assumir</Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Pagination page={data.page} limit={data.limit} total={data.total} onPage={setPage} label="tickets" testid="staff-pager" />
    </div>
  );
}
